import {
    X, AlertTriangle, ShieldAlert, Clock, Ticket, Server, ExternalLink, Activity
} from 'lucide-react';

interface AlertSidebarProps {
    open: boolean;
    event: any;
    onClose: () => void;
}

export default function AlertSidebar({ open, event, onClose }: AlertSidebarProps) {
    if (!open || !event) return null;

    const renderEventData = (eventDataStr: string) => {
        if (!eventDataStr) return null;
        try {
            const parsed = typeof eventDataStr === 'string' ? JSON.parse(eventDataStr) : eventDataStr;
            const items = Array.isArray(parsed) ? parsed : [parsed];
            return items.map((item, idx) => {
                const dataObj = item.data || item;
                return (
                    <div key={idx} className="bg-gray-900/80 border border-gray-800 rounded-xl p-3.5 space-y-3 shadow-inner">
                        {item.status && (
                            <div className="flex justify-between border-b border-gray-800 pb-2 mb-2 font-black text-[10px] uppercase tracking-wider">
                                <span className="text-gray-500">Status</span>
                                <span className={item.status === 'open' ? 'text-red-400' : 'text-emerald-400'}>{item.status}</span>
                            </div>
                        )}
                        <div className="space-y-2">
                            {Object.entries(dataObj).map(([key, val]) => {
                                const formattedKey = key.replace(/_/g, ' ').toUpperCase();

                                let displayVal = String(val);

                                if (typeof val === 'boolean') {
                                    displayVal = val ? 'TRUE' : 'FALSE';
                                }
                                else if (
                                    typeof val === 'string' &&
                                    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)
                                ) {
                                    displayVal = new Date(val)
                                        .toLocaleString('en-GB', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit',
                                            hour12: false,
                                        })
                                        .replace(',', '');
                                }

                                return (
                                    <div
                                        key={key}
                                        className="flex flex-col gap-1 border-b border-white/5 pb-2 last:border-0 last:pb-0"
                                    >
                                        <span className="text-[8px] font-black text-gray-500 uppercase tracking-widest">
                                            {formattedKey}
                                        </span>

                                        <span
                                            className={`text-[10px] font-mono break-words ${typeof val === 'boolean' && !val
                                                ? 'text-red-400 font-bold'
                                                : 'text-gray-200'
                                                }`}
                                        >
                                            {displayVal}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            });
        } catch (e) {
            return (
                <div className="bg-gray-950 p-3 rounded-lg border border-gray-800 overflow-x-auto">
                    <pre className="text-[9px] font-mono text-gray-400 whitespace-pre-wrap break-all leading-normal">
                        {eventDataStr}
                    </pre>
                </div>
            );
        }
    };

    const isCritical = event.severity === 'critical';

    return (
        <div className="fixed top-0 right-0 h-full w-[360px] bg-[#0b0e14] shadow-2xl border-l border-gray-800 z-[100] flex flex-col overflow-hidden animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className={`relative h-24 flex-shrink-0 bg-gradient-to-br ${isCritical ? 'from-red-950/40' : 'from-yellow-950/30'} to-[#0b0e14] overflow-hidden border-b border-gray-800`}>
                <div className={`absolute inset-0 bg-[radial-gradient(circle_at_30%_-20%,${isCritical ? 'rgba(239,68,68,0.15)' : 'rgba(234,179,8,0.1)'},transparent_70%)]`} />

                <div className="absolute bottom-4 left-5 right-5 flex items-end gap-3">
                    <div className={`p-3 rounded-xl shadow-lg border ${isCritical
                        ? 'bg-red-500/20 text-red-500 border-red-500/30'
                        : 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30'
                        }`}>
                        {isCritical ? <ShieldAlert className="w-5 h-5 animate-pulse" /> : <AlertTriangle className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 flex flex-col gap-1 min-w-0">
                        <div className="flex items-center justify-between w-full">
                            <h2 className="text-xs font-black text-white uppercase tracking-wider truncate max-w-[200px]">
                                Alert Details
                            </h2>
                            <button
                                onClick={onClose}
                                className="p-1 hover:bg-white/5 rounded-lg border border-gray-800 text-gray-400 hover:text-white transition-all cursor-pointer"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider ${isCritical
                                ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                                : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                                }`}>
                                {event.severity || 'Warning'}
                            </span>
                            <span className="px-1.5 py-0.5 bg-gray-800 text-gray-400 border border-gray-700 rounded text-[8px] font-black uppercase tracking-wider">
                                {event.event_status || 'Open'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto custom-scroll p-5 space-y-5">
                {/* Alert Message */}
                <div className={`p-3.5 rounded-xl border ${isCritical
                    ? 'bg-red-500/5 border-red-500/10 text-red-200'
                    : 'bg-yellow-500/5 border-yellow-500/10 text-yellow-200'
                    }`}>
                    <h4 className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Message</h4>
                    <p className="text-xs font-bold leading-relaxed">{event.message}</p>
                </div>

                {/* Description */}
                {event.description && (
                    <div className="bg-gray-900/40 p-3.5 rounded-xl border border-gray-800/80">
                        <h4 className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1.5">Description</h4>
                        <p className="text-xs text-gray-300 leading-relaxed font-medium">{event.description}</p>
                    </div>
                )}

                {/* Asset info */}
                <div className="space-y-2.5">
                    <div className="flex items-center gap-1.5 px-1">
                        <Server className="w-3.5 h-3.5 text-blue-500" />
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Asset Information</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {event.asset_hostname && (
                            <div className="bg-gray-900/40 p-2.5 rounded-xl border border-gray-800/50">
                                <span className="text-[8px] font-black text-gray-500 uppercase tracking-wider">Hostname</span>
                                <p className="text-[10px] font-bold text-gray-200 truncate mt-0.5">{event.asset_hostname}</p>
                            </div>
                        )}
                        {event.asset_ip && (
                            <div className="bg-gray-900/40 p-2.5 rounded-xl border border-gray-800/50">
                                <span className="text-[8px] font-black text-gray-500 uppercase tracking-wider">IP Address</span>
                                <p className="text-[10px] font-bold text-blue-400 font-mono mt-0.5">{event.asset_ip}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Ticket Details */}
                {event.ticket_number && (
                    <div className="space-y-2.5">
                        <div className="flex items-center gap-1.5 px-1">
                            <Ticket className="w-3.5 h-3.5 text-orange-500" />
                            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Ticket Reference</h3>
                        </div>
                        <div className="bg-gray-900/40 p-3.5 rounded-xl border border-gray-800/80 flex items-center justify-between">
                            <div>
                                <span className="text-[8px] font-black text-gray-500 uppercase tracking-wider">Ticket ID</span>
                                <p className="text-xs font-bold text-orange-400 font-mono mt-0.5">
                                    {typeof event.ticket_number === 'object'
                                        ? (event.ticket_number as any).ticket_number || (event.ticket_number as any).ticket_id || JSON.stringify(event.ticket_number)
                                        : String(event.ticket_number)}
                                </p>
                            </div>
                            {event.ticket_url && (
                                <a
                                    href={event.ticket_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3 py-1.5 flex items-center gap-1 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-lg text-[9px] font-black text-orange-400 transition-all uppercase tracking-wider"
                                >
                                    View Ticket <ExternalLink className="w-3 h-3" />
                                </a>
                            )}
                        </div>
                    </div>
                )}

                {/* Event Metadata (Timestamps) */}
                <div className="space-y-2.5">
                    <div className="flex items-center gap-1.5 px-1">
                        <Clock className="w-3.5 h-3.5 text-gray-400" />
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Timestamps</h3>
                    </div>
                    <div className="bg-gray-900/40 p-3.5 rounded-xl border border-gray-800/80 space-y-3 text-[10px] font-medium text-gray-300">
                        {event.created && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Created</span>
                                <span>
                                    {new Date(`${event.created}Z`)
                                        .toLocaleString("en-GB", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            second: "2-digit",
                                            hour12: false,
                                        })
                                        .replace(",", "")}
                                </span>
                            </div>
                        )}
                        {event.updated && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Updated</span>
                                <span>
                                    {new Date(`${event.updated}Z`)
                                        .toLocaleString("en-GB", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            second: "2-digit",
                                            hour12: false,
                                        })
                                        .replace(",", "")}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Diagnostics Event Data */}
                {event.event_data && (
                    <div className="space-y-2.5 pb-2">
                        <div className="flex items-center gap-1.5 px-1">
                            <Activity className="w-3.5 h-3.5 text-red-400" />
                            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Diagnostic Details</h3>
                        </div>
                        {renderEventData(event.event_data)}
                    </div>
                )}
            </div>
        </div>
    );
}
